import checkAutoplay from 'web-player/autoplay';
import factory from 'state/factory';
import HighlightsSDK from 'components/Highlights/HighlightsSdk';
import LatestNews from '../LatestNews';
import ListenInApp from 'components/ListenInApp';
import LiveHero from 'views/Live/LiveHero';
import LiveHighlightsHead from 'views/Live/LiveHighlightsHead';
import LivePromotionSection from 'views/Live/LivePromotionSection';
import LiveSimilarSection from 'views/Live/LiveSimilarSection';
import LiveStationHead from 'views/Live/LiveStationHead';
import OnAirSchedule from 'views/Live/OnAirSchedule';
import PageBody, { ViewName } from 'views/PageBody';
import PLAYED_FROM from 'modules/Analytics/constants/playedFrom';
import RecentlyPlayed from 'views/Live/RecentlyPlayed';
import RelatedPlaylists from '../RelatedPlaylists';
import RelatedPodcasts from '../RelatedPodcasts';
import Section from 'components/Section';
import withAnalytics from 'modules/Analytics/withAnalytics';
import { AbTestGroups } from 'state/User/types';
import { calculateSlideDimensions } from 'utils/calculateSlideDimensions';
import { Genre } from 'state/Genres/types';
import { getHighlightsConfig } from 'state/Config/selectors';
import { getHighlightsSDKEnabled } from 'state/Features/selectors';
import { getIsMobile } from 'state/Environment/selectors';
import { getUserType } from 'state/User/selectors';
import { getVideoMapStatus } from 'state/Live/selectors';
import { LiveStation } from 'state/Live/types';
import { openSignupModal } from 'state/UI/actions';
import { requestRelatedPlaylists } from 'state/Playlist/actions';
import { slugify } from 'utils/string';
import { STATION_TYPE } from 'constants/stationTypes';
import { SUBSCRIPTION_TYPE } from 'constants/subscriptionConstants';
import { useAdsContext } from 'ads/components/AdsProvider';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslate } from 'widget/hooks';
import { WIDGET_DIMENSIONS } from 'constants/widgetDimensions';
import type { SocialProps } from 'components/Social/types';

type Props = {
  abTestGroups: AbTestGroups;
  ads?: {
    provider_id?: string;
    enable_triton_token?: string;
    audio_ad_provider?: string;
  };
  callLetters?: string;
  currentStation: LiveStation;
  hasLocalSocial: boolean;
  pagePath: string;
  seedId: number;
  stationLoaded: boolean;
  genres?: Array<Genre>;
  queryParam: {
    showRegGate: boolean;
  };
  routeParams: {
    highlightsSlug?: string;
    [key: string]: string | undefined;
  };
};

const LIVE_PROFILE_LIMIT = 5;

const store = factory();

const openSignup = (context: string) => openSignupModal({ context });

const HIGHLIGHTS_TEST_GROUP = 'webLegacyHighlights' as const;

function LiveProfile(props: Props) {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const isMobile = useSelector(getIsMobile);
  const userType = useSelector(getUserType);
  const highlightsConfig = useSelector(getHighlightsConfig);
  const [noAds, setNoAds] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const { isAdsEnabled } = useAdsContext();
  const {
    abTestGroups,
    currentStation,
    hasLocalSocial,
    pagePath,
    seedId,
    stationLoaded,
    queryParam,
    routeParams,
  } = props;
  const isHighlightsSDKEnabled = useSelector(getHighlightsSDKEnabled);
  const hasStationVideos = useSelector((state: any) =>
    getVideoMapStatus(state, { stationId: seedId }),
  );
  const [showHighlights, setShowHighlights] = useState(false);

  // Check if highlights config has valid values (not just empty defaults)
  const hasValidHighlightsConfig = useMemo(
    () =>
      Boolean(
        highlightsConfig?.mobile?.apiKey && highlightsConfig?.desktop?.apiKey,
      ),
    [highlightsConfig?.desktop?.apiKey, highlightsConfig?.mobile?.apiKey],
  );

  useEffect(() => {
    setShowHighlights(() => {
      const testGroupMembership = abTestGroups?.[HIGHLIGHTS_TEST_GROUP] === 'B';

      return (
        isHighlightsSDKEnabled &&
        hasStationVideos &&
        hasValidHighlightsConfig &&
        testGroupMembership
      );
    });
  }, [
    abTestGroups,
    hasStationVideos,
    hasValidHighlightsConfig,
    isHighlightsSDKEnabled,
  ]);

  const { seedType = 'live', stationName = '' } = currentStation;

  const clipSlugAndId = routeParams?.highlightsSlug;

  if (queryParam.showRegGate) {
    store.dispatch(openSignup(queryParam.showRegGate?.toString()));
  }

  useEffect(() => {
    if (isMobile) {
      setContainerWidth(window.innerWidth);
    }
  }, [isMobile]);

  const { slideHeight: mobileSlideHeight } = calculateSlideDimensions({
    containerDimension: containerWidth,
    dimensionType: 'width',
    aspectRatio: 1,
    slidesPerView: 1.2,
  });

  const social: SocialProps = {
    dimensions: WIDGET_DIMENSIONS.LIVE,
    seedId,
    seedType,
    stationName,
    supportsConnect: hasLocalSocial,
    supportsShare: true,
    url: `/live/${slugify(stationName)}-${seedId}/`,
  };

  // Use highlights config from centralized package with mobile height override
  const highlightsSdkInitData = {
    mobile: {
      ...highlightsConfig.mobile,
      height: mobileSlideHeight,
    },
    desktop: highlightsConfig.desktop,
  };

  // IHRWEB-16968: force pagebody rerender after hydration so that AA users get full page view
  useLayoutEffect(() => {
    setNoAds(userType === SUBSCRIPTION_TYPE.PREMIUM);
  }, []);

  useEffect(() => {
    if (!stationLoaded) return;
    checkAutoplay({ seedId, seedType: STATION_TYPE.LIVE });
  }, [stationLoaded, seedId]);

  useEffect(() => {
    dispatch(requestRelatedPlaylists(currentStation.relatedPlaylistIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStation.id, dispatch]);

  return (
    <>
      {clipSlugAndId ?
        <LiveHighlightsHead seedId={seedId} seedType={seedType} />
      : <LiveStationHead seedId={seedId} seedType={seedType} />}
      {seedId && (
        <>
          <LiveHero social={social} />
          <ListenInApp seedId={seedId} seedType={seedType} />
          <PageBody dataTest={ViewName.LiveProfile} noAds={noAds}>
            <OnAirSchedule clickedFrom="live_profile|on_air|tile" />
            <RecentlyPlayed
              itemSelectedLocation="live_profile|recently_played|tile"
              key="recentlyPlayed"
              playedFrom={PLAYED_FROM.PROF_LIVE_RECENT}
              seedId={seedId}
            />
            {showHighlights ?
              <Section
                data-test={`${ViewName.LiveProfile}-section-highlights`}
                header={translate('Highlights')}
              >
                <HighlightsSDK
                  clipSlugAndId={clipSlugAndId}
                  highlightsSDKSourceType="legacy"
                  isAdsEnabled={isAdsEnabled}
                  stationId={seedId}
                  stationName={stationName}
                  {...(isMobile ?
                    highlightsSdkInitData.mobile
                  : highlightsSdkInitData.desktop)}
                />
              </Section>
            : null}
            <LivePromotionSection />
            <RelatedPodcasts
              stationName={stationName}
              tilesInRow={LIVE_PROFILE_LIMIT}
            />
            <RelatedPlaylists
              stationName={stationName}
              tilesInRow={LIVE_PROFILE_LIMIT}
            />
            <LiveSimilarSection
              hasBottomMargin={false}
              isAbTest
              itemSelectedLocation="live_profile|similar_stations|tile"
              playedFrom={PLAYED_FROM.PROF_LIVE_MAIN_SIMILAR}
              singleRow
              stationId={seedId}
              stationName={stationName}
              stationType={seedType}
              tilesInRow={LIVE_PROFILE_LIMIT}
              url={`${pagePath}similar/`}
            />
            <LatestNews clickedFrom="live_profile|the_latest|tile" />
          </PageBody>
        </>
      )}
    </>
  );
}

export default withAnalytics(
  ({ callLetters, currentStation, seedId, genres, ads }: Props) => ({
    id: `live|${seedId}`,
    name: callLetters,
    stationCallLetter: callLetters,
    stationFormat: currentStation?.format,
    stationMarket: currentStation?.markets,
    pageName: 'live_profile',
    stationName: currentStation?.stationName,
    genres,
    providerId: ads?.provider_id,
    enableTritonToken: ads?.enable_triton_token,
  }),
  {
    trackOnDidUpdate: (prevProps: Props, nextProps: Props) =>
      !!nextProps.seedId &&
      String(nextProps.seedId) !== String(prevProps.seedId),
    trackOnWillMount: ({ seedId }: Props) => !!seedId,
  },
)(LiveProfile);
